import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #gridObserver = new MutationObserver(() => {
  this.#syncProductCardImageSizes();
  this.#syncLoadMoreButton();
});

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeGrid();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
    this.#syncLoadMoreButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver.disconnect();
    this.#defaultImageSizes = new WeakMap();
  }
/**
 * Loads the next collection page using the theme's existing loader.
 */
async loadMore() {
  const button = this.querySelector('[data-hero-load-more]');
  const status = this.querySelector('[data-hero-load-more-status]');
  const grid = this.refs.grid;

  if (!(button instanceof HTMLButtonElement) || !grid) return;
  if (button.disabled) return;

  const previousCount = grid.querySelectorAll('[data-product-id]').length;

  button.disabled = true;
  button.textContent = 'Chargement...';
  grid.setAttribute('aria-busy', 'true');
  if (status) status.textContent = '';

  try {
    const cards = grid.querySelectorAll('[data-page][data-product-id]');
const lastCard = cards[cards.length - 1];

if (!lastCard) {
  throw new Error('Aucun produit trouvé dans la grille.');
}

const nextPage = Number(lastCard.getAttribute('data-page')) + 1;
const sectionId = this.getAttribute('section-id');

if (!sectionId) {
  throw new Error('Identifiant de section manquant.');
}

// Conserve les filtres et le tri de la collection.
const nextUrl = new URL(window.location.href);
nextUrl.searchParams.set('page', String(nextPage));
nextUrl.searchParams.set('section_id', sectionId);
nextUrl.hash = '';

const response = await fetch(nextUrl.toString(), {
  signal: AbortSignal.timeout(20000),
});

if (!response.ok) {
  throw new Error(`Erreur de chargement : ${response.status}`);
}

const html = await response.text();

// Ne rien ajouter si les filtres ont remplacé la grille entre-temps.
if (!button.isConnected || this.refs.grid !== grid) return;

const page = new DOMParser().parseFromString(html, 'text/html');
const nextGrid = page.querySelector('[ref="grid"]');

if (!nextGrid) {
  throw new Error('Grille absente de la réponse.');
}

const existingIds = new Set(
  Array.from(grid.querySelectorAll('[data-product-id]'))
    .map((card) => card.getAttribute('data-product-id'))
);

const newCards = Array.from(nextGrid.children).filter(
  (card) =>
    card.matches('[data-product-id]') &&
    !existingIds.has(card.getAttribute('data-product-id'))
);

if (!newCards.length) {
  throw new Error('Aucun nouveau produit reçu.');
}

grid.append(...newCards);


    // The filters may have replaced the grid during the request.
    if (!button.isConnected || this.refs.grid !== grid) return;

    const currentCount = grid.querySelectorAll('[data-product-id]').length;
    const addedCount = currentCount - previousCount;

    if (status) {
      status.textContent = addedCount > 0
        ? `${addedCount} produits supplémentaires affichés.`
        : 'Chargement impossible. Veuillez réessayer.';
    }
  } catch (error) {
    console.error('Collection load more:', error);
    if (status) {
      status.textContent = 'Chargement impossible. Veuillez réessayer.';
    }
  } finally {
    grid.removeAttribute('aria-busy');
    if (button.isConnected) {
      button.disabled = false;
      button.textContent = 'Voir plus...';
      this.#syncLoadMoreButton();

      if (button.hidden && status instanceof HTMLElement) {
        status.focus({ preventScroll: true });
      }
    }
  }
}

/**
 * Hides the button when the last product page is displayed.
 */
#syncLoadMoreButton() {
  const button = this.querySelector('[data-hero-load-more]');
  const grid = this.refs.grid;

  if (!(button instanceof HTMLButtonElement) || !grid) return;

  const cards = grid.querySelectorAll('[data-page][data-product-id]');
  const lastCard = cards[cards.length - 1];
  const lastPage = Number(grid.dataset.lastPage);

  button.hidden = !lastCard ||
    Number(lastCard.getAttribute('data-page')) >= lastPage;
}
  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#syncProductCardImageSizes();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const useSingleColumnSize = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = useSingleColumnSize ? '100vw' : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
